import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, LogOut, Activity, ChevronRight, Trophy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import logoEkose from '../../../images/logoekose.svg';
import type { User } from '../../types/User';

export interface NavItem {
  name: string;
  icon: React.ReactNode;
  path: string;
}

interface DashboardSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  navItems: NavItem[];
  userType: 'patient' | 'doctor' | 'admin' | 'nurse' | 'clinic';
  user: User | null;
  onLogout: () => void;
}

/**
 * Sidebar navigation component with logo, nav items, and logout
 */
export const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  isOpen,
  onClose,
  navItems,
  userType,
  user,
  onLogout,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  // Calcul du score de santé si patient
  const userPreferences = user?.preferences as { onboardingProgress?: number; healthRank?: string } | undefined;
  const healthProgress = userPreferences?.onboardingProgress || 0;
  const healthRank = userPreferences?.healthRank || 'INITIE';

  return (
    <>
      {/* Backdrop for mobile */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-med-primary-darker/60 backdrop-blur-md z-40 lg:hidden"
            onClick={onClose}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <div
        id="sidebar-navigation"
        className={`fixed inset-y-0 left-0 z-50 w-full max-w-[300px] bg-white/40 backdrop-blur-3xl border-r border-white/60 transform transition-all duration-700 ease-in-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:relative lg:w-85 flex-shrink-0 shadow-[20px_0_40px_-20px_rgba(0,50,115,0.05)] overflow-hidden`}
        role="navigation"
        aria-label={t('navigation.sidebar.mainNavigation')}
      >
        {/* Dynamic Background Accents */}
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-med-primary/5 to-transparent pointer-events-none"></div>

        <div className="h-full flex flex-col relative z-10">
          {/* Brand Header Elite */}
          <div className="relative px-8 py-10 flex flex-col items-center">
            <Link to={`/${userType}`} className="flex flex-col items-center group relative" onClick={onClose}>
              <div className="w-20 h-20 bg-white rounded-[1.8rem] flex items-center justify-center shadow-2xl shadow-med-primary/10 border border-white/60 mb-5 transition-all duration-500 group-hover:rotate-3 group-hover:scale-105 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-med-primary/5 to-transparent opacity-50"></div>
                <img
                  src={logoEkose}
                  alt="Logo Ekose"
                  className="w-14 h-14 object-contain relative z-10 transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <h1 className="text-xl font-black italic tracking-tighter text-med-primary uppercase">
                Ekose<span className="text-med-primary opacity-30">.</span><span className="text-med-success">Rx</span>
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-1 h-1 bg-med-success rounded-full animate-pulse shadow-[0_0_8px_theme(colors.med.success)]"></div>
                <span className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-500 italic">{t('navigation.sidebar.elitePlatform')}</span>
              </div>
            </Link>

            {/* Close button for mobile */}
            <button
              className="absolute right-4 top-4 p-2.5 rounded-xl text-slate-400 hover:text-med-primary bg-white/50 lg:hidden focus:outline-none transition-all hover:rotate-90 active:scale-90"
              onClick={onClose}
              aria-label={t('navigation.sidebar.closeMenu')}
            >
              <X size={18} />
            </button>
          </div>

          {/* Navigation Elite */}
          <nav
            className="flex-1 py-4 px-5 space-y-2 overflow-y-auto custom-scrollbar"
            role="navigation"
            aria-label={t('navigation.sidebar.mainMenu')}
          >
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              
              let testId = `${item.name.toLowerCase()}-menu`;
              if (item.path.includes('appointment')) {
                testId = 'consultations-menu';
              } else if (item.path.includes('patient-records') || item.path.includes('medical-records') || item.path.includes('patients')) {
                testId = 'patients-menu';
              } else if (item.path.includes('messages')) {
                testId = 'chat-menu';
              } else if (item.path.includes('doctors')) {
                testId = 'doctors-menu';
              } else if (item.path.includes('settings')) {
                testId = 'settings-menu';
              } else if (item.path === '/doctor' || item.path === '/patient' || item.path === '/admin') {
                testId = 'dashboard-menu';
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className="group block relative"
                  aria-current={isActive ? 'page' : undefined}
                  onClick={onClose}
                  data-testid={testId}
                >
                  <div className={`flex items-center px-5 py-3.5 rounded-[1.5rem] transition-all duration-500 relative overflow-hidden ${
                    isActive
                      ? 'bg-gradient-to-br from-med-primary to-med-primary-dark text-white shadow-2xl shadow-med-primary/30'
                      : 'text-slate-500 hover:bg-white hover:text-med-primary hover:shadow-xl'
                  }`}>
                    {isActive && (
                      <motion.div 
                        layoutId="active-bar-layout"
                        className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-med-success rounded-full shadow-[0_0_15px_theme(colors.med.success)]"
                      />
                    )}
                    
                    <span className={`mr-4 transition-all duration-500 ${isActive ? 'text-med-success' : 'text-slate-400 group-hover:text-med-primary'}`} aria-hidden="true">
                      {React.cloneElement(item.icon as React.ReactElement, { size: 18, strokeWidth: isActive ? 2.5 : 2 })}
                    </span>
                    
                    <span className="text-[11px] font-black tracking-tight italic flex-1 uppercase">
                      {item.name}
                    </span>

                    {isActive && <ChevronRight size={12} className="text-med-success opacity-40" />}
                  </div>
                </Link>
              );
            })}
          </nav>

          {/* Status Card & Logout */}
          <div className="p-6 mt-auto space-y-4 relative">
            {/* Intelligent Status Card */}
            {userType !== 'admin' && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-med-primary rounded-3xl text-white shadow-2xl shadow-med-primary/20 relative overflow-hidden group/status"
              >
                <div className="absolute top-0 right-0 w-16 h-16 bg-white/5 rounded-bl-[40px] pointer-events-none"></div>
                <div className="relative z-10">
                  {userType === 'patient' ? (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                           <Trophy size={14} className="text-med-success" />
                           <span className="text-[9px] font-black uppercase tracking-widest text-med-success italic">{t('navigation.sidebar.healthLevel')}</span>
                        </div>
                        <span className="text-[9px] font-bold text-white/60">{Math.round(healthProgress)}%</span>
                      </div>
                      <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden mb-2">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${healthProgress}%` }}
                          className="h-full bg-med-success"
                        />
                      </div>
                      <p className="text-[10px] font-black italic uppercase tracking-tighter">
                        {healthRank === 'ELITE' 
                          ? t('navigation.sidebar.healthRanks.elite') 
                          : healthRank === 'ADEPTE' 
                            ? t('navigation.sidebar.healthRanks.adept') 
                            : t('navigation.sidebar.healthRanks.initiate')}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                         <div className={`w-2 h-2 rounded-full animate-pulse ${user?.availabilityStatus === 'AVAILABLE' ? 'bg-med-success shadow-[0_0_8px_theme(colors.med.success)]' : 'bg-amber-500 shadow-[0_0_8px_theme(colors.med.warning)]'}`}></div>
                         <span className="text-[9px] font-black uppercase tracking-widest text-white italic">{t('navigation.sidebar.onlineCabinet')}</span>
                      </div>
                      <p className="text-[10px] font-bold text-white/60 mb-2 uppercase tracking-tighter">{t('navigation.sidebar.currentStatus')}</p>
                      <div className="px-3 py-1 bg-white/10 rounded-lg inline-block text-[9px] font-black uppercase italic text-med-success">
                        {user?.availabilityStatus || t('navigation.sidebar.offline')}
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            )}

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onLogout}
              data-testid="logout-button"
              className="group w-full flex items-center justify-center px-6 py-4 rounded-2xl bg-rose-50/50 border border-rose-100 text-red-500 hover:bg-red-500 hover:text-white transition-all duration-500 gap-3 hover:shadow-xl hover:shadow-red-500/20"
              aria-label={t('navigation.sidebar.logout')}
            >
              <LogOut size={16} className="group-hover:rotate-12 transition-transform" aria-hidden="true" />
              <span className="text-[9px] font-black uppercase tracking-[0.2em] italic">{t('navigation.sidebar.logout')}</span>
            </motion.button>
            
            <div className="flex justify-center opacity-10 pointer-events-none pb-2">
              <Activity size={32} className="text-med-primary" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
